/* eslint-disable filenames/match-exported */
import Model from 'echarts/lib/model/Model';
import SeriesModel from 'echarts/lib/model/Series';
import type SeriesData from 'echarts/lib/data/SeriesData';
import Tree, { TreeNode } from 'echarts/lib/data/Tree';
import { TreeSeriesLeavesOption } from 'echarts/lib/chart/tree/TreeSeries';
import type {
  SeriesOption,
  BoxLayoutOptionMixin,
  RoamOptionMixin,
  ZRColor
} from 'echarts/lib/util/types';
import { BoundingRect } from 'echarts/lib/util/graphic';
import { graphic } from 'echarts';
import { Path, PathProps, TextProps, Line, LineProps, PolygonProps } from 'zrender';
import { ECSymbol } from 'echarts/lib/util/symbol';

type TreeType = Tree;

export type RelationTreeNode = TreeNode & {
  index: number;
  open?: boolean;
  nodeElGroup: graphic.Group;
  lineElGroup?: graphic.Group;
};

export interface GroupNodeArray {
  [key: number | string]: RelationTreeNode[];
}

type RenderOption = {
  text?: {
    style?: {
      text: string;
    };
  };
  attachment?: {
    style?: {
      text?: string;
    };
  }[];
  logo?: string;
};
export interface RelationshipDiagramSeriesNodeItemOption {
  type?: string;
  /** 优先级 */
  priority?: number;
  /** 节点名字 */
  name?: string;
  /** 水印 */
  watermark?: string;
  /** 节点背景颜色 */
  backgroundColor?: string[];
  /** 渲染配置 */
  render?: RenderOption | RenderOption[];
  /** 孩子节点 */
  children?: RelationshipDiagramSeriesNodeItemOption[];
  /** 是否折叠节点 */
  collapsed?: boolean;
  /** 父节点id */
  parentId?: string | number;
  /** 默认的主树 */
  __treeIndex?: number;
}

export interface RelationshipDiagramSeriesOption
  extends SeriesOption,
    BoxLayoutOptionMixin,
    RoamOptionMixin {
  type?: 'dvRelationshipDiagram';
  data?: RelationshipDiagramSeriesNodeItemOption[];
  /** 初始展开的树深度（根节点的深度可认为为 0） */
  initialTreeDepth?: number;
  /** 是否折叠节点 */
  collapsed?: boolean;
  /** 背景颜色 */
  dvBackground?: {
    show?: boolean;
    color?: string[];
    padding?: number[];
  };
  /** 节点渲染方法 */
  dvNodeRender?: (
    originNodeData?: RelationshipDiagramSeriesNodeItemOption,
    createEl?: (elOption: { type: string }) => Path<PathProps>,
    group?: graphic.Group,
    createSymbol?: (
      symbolType: string,
      x: number,
      y: number,
      w: number,
      h: number,
      color?: ZRColor,
      keepAspect?: boolean
    ) => ECSymbol,
    node?: RelationTreeNode
  ) => graphic.Group;
  dvLineRender?: (
    originNodeData?: RelationshipDiagramSeriesNodeItemOption,
    createEl?: (elOption: { type: string }) => Path<PathProps>,
    group?: graphic.Group,
    node?: RelationTreeNode
  ) => graphic.Group;
  /** 节点的间距 */
  dvNodeGap?: (
    originNodeData?: RelationshipDiagramSeriesNodeItemOption,
    node?: RelationTreeNode,
    spaceArray?: number[],
    widthArray?: number[]
  ) => void;
  /** 水印 */
  dvWatermark?: {
    show?: boolean;
    watermark?: TextProps[];
  };
  /** 同级分支线 */
  dvLevelLine?: {
    show?: boolean;
    padding?: (
      originNodeData?: RelationshipDiagramSeriesNodeItemOption,
      node?: RelationTreeNode
    ) => number[];
    style?: Line;
  };
  /** 每一级节点竖直方向的间距 */
  dvVerticalSpacingBetweenNode?: number[];
  /** 首屏自适应 */
  dvAdapt?: boolean;
  /** 导向线 */
  dvToNextTreeLine?: {
    show?: boolean;
    lineStyle?: LineProps & {
      lineWidth: number;
      stroke: string;
    };
    arrowStyle: PolygonProps & {
      lineWidth: number;
      fill: string;
      scale: number;
    };
  };
  leaves?: TreeSeriesLeavesOption;
}

// eslint-disable-next-line no-use-before-define
export type RelationshipDiagramSeriesModelClazz = RelationshipDiagramSeriesModel;

export default class RelationshipDiagramSeriesModel extends SeriesModel<RelationshipDiagramSeriesOption> {
  static readonly type = 'series.dvRelationshipDiagram';

  readonly type = RelationshipDiagramSeriesModel.type;

  static defaultOption: RelationshipDiagramSeriesOption = {
    type: 'dvRelationshipDiagram',
    // 默认坐标系
    coordinateSystem: 'view',

    dvAdapt: true,

    dvBackground: {
      show: false,
      padding: [0, 0, 0, 0]
    },

    dvWatermark: {
      show: false
    },

    dvLevelLine: {
      show: false
    },

    dvToNextTreeLine: {
      show: false,
      lineStyle: {
        lineWidth: 0.5,
        stroke: 'rgba(0, 0, 0, 0.24)'
      },
      arrowStyle: {
        lineWidth: 1,
        fill: 'rgba(0, 0, 0, 0.24)',
        scale: 1
      }
    }
  };

  /**
   * Prevent from auto set z, zlevel, z2 by the framework.
   */
  preventAutoZ = true;

  // 所有树中的最大深度
  maxDepth = 0;

  // 有几棵树
  treesLength = 0;

  // 按列收集组节点（node.depth为奇数的节点）
  groupNodeArray: GroupNodeArray = {};

  // 子树的最大宽度
  treeMaxX: number[];

  // 子树的最大深度
  treeMaxY: number[];

  layoutInfo: BoundingRect;

  trees: TreeType<RelationshipDiagramSeriesModelClazz>[];

  getInitialData(
    this: RelationshipDiagramSeriesModelClazz,
    option: RelationshipDiagramSeriesOption
  ): SeriesData {
    // eslint-disable-next-line no-console

    // if (ecModel.option.dvAnimationDuration) {
    //   this.dvAnimationDuration = ecModel.option.dvAnimationDuration as number;
    // }
    const rootItems = option.data;

    const trees: TreeType<RelationshipDiagramSeriesModelClazz>[] = rootItems.map(
      (rootItem, treeIndex) => {
        // 获取树结构原始信息
        const root: RelationshipDiagramSeriesNodeItemOption = {
          name: option.name as string,
          children: [{ ...rootItem, children: rootItem.children, type: 'root' }]
        };

        const leaves = option.leaves || {};

        const leavesModel = new Model(leaves, this, this.ecModel);

        // 创建树
        const tree = this.createRelationTree(root, leavesModel);
        let treeDepth = 0;

        // 计算树的最大深度
        tree.eachNode('preorder', node => {
          if (node.depth > treeDepth) {
            treeDepth = node.depth;
          }
        });
        const expandTreeDepth = 1;
        this.maxDepth = Math.max(this.maxDepth, treeDepth);

        // 初始化树的展开层数
        tree.root.eachNode('preorder', node => {
          // 获取对应节点的原始信息
          const item = node.hostTree.data.getRawDataItem(
            node.dataIndex
          ) as RelationshipDiagramSeriesNodeItemOption;
          // 节点记录树的下标
          item.__treeIndex = treeIndex;

          // 节点的展开根据原始信息的collapsed字段控制
          node.isExpand =
            item && typeof item.collapsed !== 'undefined'
              ? !item.collapsed
              : node.depth <= expandTreeDepth;
        });
        return tree;
      }
    );

    this.trees = trees;
    return trees[0].data;
  }

  createRelationTree(
    root: RelationshipDiagramSeriesNodeItemOption,
    leavesModel: Model<TreeSeriesLeavesOption>
  ) {
    const tree = Tree.createTree(root, this, beforeLink);
    function beforeLink(nodeData: SeriesData) {
      nodeData.wrapMethod('getItemModel', (model: Model, idx: number) => {
        const node = tree.getNodeByDataIndex(idx);

        if (!(node && node.children.length && node.isExpand)) {
          model.parentModel = leavesModel;
        }
        return model;
      });
    }
    return tree;
  }
}
